import { db, auth } from '@/js/firebase';
import { doc, getDoc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { logger } from './loggerService';
import { toastBridge } from './toastBridge';
import { DEFAULT_SCHEDULING_RULES } from '@/constants/schedulingRules';

/**
 * Scheduling Rules Service
 * Global date-availability ruleset: which production types compete for which
 * shared resource, the client booking window, per-type default assignees, and
 * the Google Calendar availability check.
 * Document: settings/schedulingRules
 */
class SchedulingRulesService {
  constructor() {
    this.logger = logger;
    this.toast = toastBridge;
    this.docPath = 'settings/schedulingRules';
    // Short-lived cache for the write paths. Event creation reads the rules to
    // prefill assignees and must not pay a Firestore round-trip per event when
    // a batch creates a dozen of them.
    this._cache = null;
    this._cacheAt = 0;
    this._cacheTtlMs = 5 * 60 * 1000;
  }

  /**
   * Cached read for write paths. Never throws — a rules read failing must not
   * stop an event from being created.
   * @returns {Promise<Object>}
   */
  async getCachedSettings() {
    if (this._cache && Date.now() - this._cacheAt < this._cacheTtlMs) {
      return this._cache;
    }

    try {
      this._cache = await this.getSettings();
      this._cacheAt = Date.now();
    } catch (error) {
      this.logger.warn('[SchedulingRulesService] Falling back to default rules', {
        error: error.message
      });
      this._cache = this.getDefaultSettings();
      this._cacheAt = Date.now();
    }

    return this._cache;
  }

  clearCache() {
    this._cache = null;
    this._cacheAt = 0;
  }

  /**
   * The built-in ruleset — identical to the behaviour that was hardcoded in
   * bookingAvailability.js, so an absent document is a no-op.
   * @returns {Object}
   */
  getDefaultSettings() {
    return structuredClone(DEFAULT_SCHEDULING_RULES);
  }

  /**
   * A stored doc may predate a newly added key, so merge it over the defaults
   * one level deep rather than trusting it to be complete.
   * @param {Object} stored
   * @returns {Object}
   */
  withDefaults(stored) {
    const defaults = this.getDefaultSettings();
    if (!stored) return defaults;
    return {
      ...defaults,
      ...stored,
      types: { ...defaults.types, ...(stored.types || {}) },
      // Pair settings merge rather than replace, so a ruleset saved before a
      // new production type existed still gets that type's defaults.
      conflicts: { ...defaults.conflicts, ...(stored.conflicts || {}) },
      workingHours: { ...defaults.workingHours, ...(stored.workingHours || {}) },
      assigneeDefaults: { ...defaults.assigneeDefaults, ...(stored.assigneeDefaults || {}) },
      assigneeCalendarCheck: {
        ...defaults.assigneeCalendarCheck,
        ...(stored.assigneeCalendarCheck || {})
      }
    };
  }

  /**
   * Get the current scheduling rules
   * @returns {Promise<Object>}
   */
  async getSettings() {
    try {
      const docSnap = await getDoc(doc(db, this.docPath));

      if (docSnap.exists()) {
        return this.withDefaults(docSnap.data());
      }

      this.logger.info('[SchedulingRulesService] No rules found, returning defaults');
      return this.getDefaultSettings();
    } catch (error) {
      this.logger.error('[SchedulingRulesService] Failed to get rules', {
        error: error.message
      });
      throw error;
    }
  }

  /**
   * Save the scheduling rules
   * @param {Object} settings
   * @returns {Promise<{success: boolean}>}
   */
  async saveSettings(settings) {
    try {
      const user = auth.currentUser;

      await setDoc(
        doc(db, this.docPath),
        {
          ...settings,
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || null,
          updatedByName: user?.displayName || 'Unknown'
        },
        { merge: true }
      );

      this.clearCache();
      this.logger.info('[SchedulingRulesService] Rules saved successfully');
      this.toast.success('Scheduling rules saved');

      return { success: true };
    } catch (error) {
      this.logger.error('[SchedulingRulesService] Failed to save rules', {
        error: error.message
      });
      this.toast.error('Failed to save scheduling rules');
      throw error;
    }
  }

  /**
   * Subscribe to rule changes
   * @param {Function} callback - receives the merged ruleset
   * @returns {Function} Unsubscribe function
   */
  subscribeToSettings(callback) {
    return onSnapshot(
      doc(db, this.docPath),
      (docSnap) => {
        const next = docSnap.exists()
          ? this.withDefaults(docSnap.data())
          : this.getDefaultSettings();
        this._cache = next;
        this._cacheAt = Date.now();
        callback(next);
      },
      (error) => {
        // Transient drops and sign-out teardown races reconnect/end on their own.
        if (error.code === 'deadline-exceeded') {
          this.logger.warn('[SchedulingRulesService] Rules subscription error', {
            error: error.message,
            code: error.code
          });
        } else {
          this.logger.firestoreListenerError(
            '[SchedulingRulesService] Rules subscription error',
            error
          );
        }
      }
    );
  }

  /**
   * Seed the document with defaults when it doesn't exist yet. Admin-only —
   * clients just read the fallback.
   * @returns {Promise<{success: boolean, created: boolean}>}
   */
  async initializeSettings() {
    try {
      const docRef = doc(db, this.docPath);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { success: true, created: false };
      }

      await setDoc(docRef, {
        ...this.getDefaultSettings(),
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || null
      });

      this.logger.info('[SchedulingRulesService] Rules initialized with defaults');
      return { success: true, created: true };
    } catch (error) {
      this.logger.error('[SchedulingRulesService] Failed to initialize rules', {
        error: error.message
      });
      throw error;
    }
  }
}

export const schedulingRulesService = new SchedulingRulesService();
export default schedulingRulesService;
